import { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { ThemeToggle } from '../feedback/ThemeToggle'
import NotificacoesBadge from './NotificacoesBadge'
import styles from './Layout.module.css'

interface NavItem {
  to: string
  label: string
  icon: string
  placeholder?: boolean
  testId?: string
}

interface NavSection {
  label: string
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    label: 'Dashboard',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: '📊', testId: 'nav-link-dashboard' },
    ],
  },
  {
    label: 'Cadastros',
    items: [
      { to: '/empresas?papel=CLIENTE', label: 'Clientes', icon: '🏢', testId: 'nav-link-clientes' },
      { to: '/equipamentos', label: 'Equipamentos', icon: '🔧', testId: 'nav-link-equipamentos' },
    ],
  },
  {
    label: 'PMOC',
    items: [
      { to: '/pmoc', label: 'PMOCs', icon: '📋', testId: 'nav-link-pmoc' },
      {
        to: '/planos-modelo',
        label: 'Planos Modelo',
        icon: '📚',
        testId: 'nav-link-planos-modelo',
      },
    ],
  },
  {
    label: 'Execução',
    items: [
      { to: '/ordens-servico', label: 'Ordens de Serviço', icon: '⚙️', testId: 'nav-link-os' },
      { to: '/aprovacoes', label: 'Aprovações', icon: '✍️', testId: 'nav-link-aprovacoes' },
      { to: '/checklists/execucoes', label: 'Checklists', icon: '✓', testId: 'nav-link-checklists' },
      { to: '/checklists/templates', label: 'Templates', icon: '📑', testId: 'nav-link-templates' },
    ],
  },
  {
    label: 'Empresas (PJ)',
    items: [
      { to: '/empresas?papel=PLANEJADOR', label: 'Planejadores', icon: '🏛️' },
      { to: '/empresas?papel=EXECUTOR', label: 'Executores', icon: '🔧' },
      { to: '/empresas?papel=LABORATORIO', label: 'Laboratórios', icon: '🧪' },
      { to: '/empresas', label: 'Todas (unificada)', icon: '🏢' },
    ],
  },
  {
    label: 'Relatórios',
    items: [
      { to: '/relatorios', label: 'Relatórios', icon: '📈', placeholder: true, testId: 'nav-link-relatorios' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: '👥', testId: 'nav-link-usuarios' },
      { to: '/auditoria', label: 'Auditoria', icon: '🔍', testId: 'nav-link-auditoria' },
      { to: '/sistema', label: 'Saúde do sistema', icon: '🩺', testId: 'nav-link-sistema' },
      { to: '/configuracoes', label: 'Configurações', icon: '⚙️', testId: 'nav-link-configuracoes' },
    ],
  },
  {
    label: 'Ajuda',
    items: [
      { to: '/onboarding', label: 'Configuração inicial', icon: '🚀', testId: 'nav-link-onboarding' },
      { to: '/manual', label: 'Manual de uso', icon: '📚', testId: 'nav-link-manual' },
    ],
  },
]

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)

  const burgerRef = useRef<HTMLButtonElement>(null)
  const navRef = useRef<HTMLElement>(null)
  // Lembra do mobileOpen anterior pra distinguir mount (false→false) de
  // toggle real (false→true ou true→false). Evita roubar foco no mount.
  const prevOpenRef = useRef(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Fecha drawer ao trocar de rota; foco do burger NÃO é restaurado aqui
  // porque a navegação já move o foco implicitamente para a próxima página.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Fecha com ESC; restaura foco ao burger (interação iniciada pelo usuário)
  const closeAndReturnFocus = useCallback(() => {
    setMobileOpen(false)
    burgerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeAndReturnFocus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen, closeAndReturnFocus])

  // Focus management — quando abre, foco no primeiro link acionável da nav.
  // Não restaura foco no fechamento por route-change pra não atropelar a
  // navegação; ESC e overlay já chamam closeAndReturnFocus diretamente.
  useEffect(() => {
    if (mobileOpen && !prevOpenRef.current) {
      const firstLink = navRef.current?.querySelector<HTMLElement>('a, button')
      firstLink?.focus()
    }
    prevOpenRef.current = mobileOpen
  }, [mobileOpen])

  return (
    <div className={styles.container} data-mobile-open={mobileOpen}>
      {/* Skip link — visível só em foco via teclado. Ajuda screen readers
          e usuários keyboard-only a pular direto para o conteúdo principal. */}
      <a href="#main-content" className={styles.skipLink} data-testid="skip-link">
        Pular para o conteúdo
      </a>
      <button
        ref={burgerRef}
        type="button"
        className={styles.burger}
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
        aria-expanded={mobileOpen}
        aria-controls="nav-main"
        data-testid="nav-burger"
      >
        <span aria-hidden="true">☰</span>
      </button>

      <div
        className={styles.overlay}
        onClick={closeAndReturnFocus}
        aria-hidden="true"
        data-testid="nav-overlay"
      />

      <aside className={styles.sidebar} aria-label="Navegação principal">
        <div className={styles.brand}>
          <span className={styles.brandIcon} aria-hidden="true">❄️</span>
          <span className={styles.brandName}>PMOC_123</span>
        </div>
        <nav ref={navRef} id="nav-main" className={styles.nav} data-testid="nav-main">
          {navSections.map((section) => (
            <div key={section.label} className={styles.navSection}>
              <span className={styles.navSectionLabel}>{section.label}</span>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.navItemActive : ''} ${item.placeholder ? styles.navItemPlaceholder : ''}`
                  }
                >
                  <span className={styles.navIcon} aria-hidden="true">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className={styles.userSection} data-testid="nav-user-menu">
          <Link
            to="/perfil"
            className={styles.userInfo}
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
            title="Meu perfil"
            data-testid="nav-link-perfil"
          >
            <span className={styles.userName}>{user?.nomeCompleto ?? user?.email}</span>
            <span className={styles.userPerfil}>{user?.perfil}</span>
          </Link>
          <NotificacoesBadge />
          <ThemeToggle />
          <button
            onClick={handleLogout}
            className={styles.logoutBtn}
            title="Sair"
            aria-label="Sair"
            data-testid="nav-logout"
          >
            ↩
          </button>
        </div>
      </aside>
      <main id="main-content" className={styles.main} tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
